// src/pages/SchedulePage.tsx

import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useGetEventsQuery, useCreateEventMutation, useDeleteEventMutation } from '../api/eventsApi';
import { useGetContactsQuery } from '../api/contactsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import EventDetailEnrichment from '../components/schedule/EventDetailEnrichment';
import type { Event, EventKind, EventCategory } from '../types/api';

const KIND_CONFIG: Record<EventKind, { label: string; color: string; bgColor: string }> = {
  meeting: { label: 'Meeting', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  meetup: { label: 'Meetup', color: 'text-purple-700', bgColor: 'bg-purple-100' },
  webinar: { label: 'Webinar', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  other: { label: 'Other', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

const CATEGORY_CONFIG: Record<EventCategory, { label: string; color: string }> = {
  virtual: { label: 'Virtual', color: 'text-blue-600' },
  in_person: { label: 'In-person', color: 'text-green-600' },
  hybrid: { label: 'Hybrid', color: 'text-purple-600' },
};

type ViewMode = 'list' | 'grouped';
/** Compact primary type filter for the unified Schedule surface (absorbs Meetups). */
type KindFilter = EventKind | 'all';

export default function SchedulePage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Deep-link: highlight specific event when ?eventId= is present
  const { search } = useLocation();
  const deepLinkEventId = new URLSearchParams(search).get('eventId');
  const highlightRef = useRef<HTMLDivElement>(null);

  // Compact primary type filter (All / Meetings / Meetups). Seeded from the
  // `?kind=` query so the legacy /meetups route redirect lands pre-filtered.
  const KIND_FILTERS: { key: KindFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'meeting', label: 'Meetings' },
    { key: 'meetup', label: 'Meetups' },
  ];
  const initialKind = new URLSearchParams(search).get('kind');
  const [kindFilter, setKindFilter] = useState<KindFilter>(
    initialKind === 'meeting' || initialKind === 'meetup' || initialKind === 'webinar' || initialKind === 'other'
      ? (initialKind as EventKind)
      : 'all'
  );

  // Phase D — when opened from a conversation/contact ("?new=1&contactId=…&
  // conversationThreadId=…&kind=…&zoom=1"), reuse THIS canonical create flow,
  // prefilled, and carry the linkage into createEvent. No separate creation path.
  const prefillContactId = new URLSearchParams(search).get('contactId') || undefined;
  const prefillConversationThreadId = new URLSearchParams(search).get('conversationThreadId') || undefined;
  const shouldPrefillNew = new URLSearchParams(search).get('new') === '1';

  useEffect(() => {
    if (deepLinkEventId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [deepLinkEventId, highlightRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default date range: today to 30 days from now
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const defaultFrom = today.toISOString().split('T')[0];
  const thirtyDaysLater = new Date(today);
  thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
  const defaultTo = thirtyDaysLater.toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);

  const {
    data: events,
    isLoading,
    error,
    refetch,
  } = useGetEventsQuery(
    {
      workspaceId,
      from: new Date(dateFrom).toISOString(),
      to: new Date(dateTo + 'T23:59:59').toISOString(),
    },
    { skip: !workspaceId }
  );

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    kind: 'meeting' as EventKind,
    category: 'virtual' as EventCategory,
    startAt: '',
    endAt: '',
    locationName: '',
    locationAddress: '',
    onlineUrl: '',
    zoomSyncEnabled: false,
    eventbriteSyncEnabled: false,
  });

  // Phase D — open the canonical create modal prefilled when arriving from a
  // conversation/contact. Runs once; the linkage ids are carried at submit time.
  useEffect(() => {
    if (!shouldPrefillNew) return;
    const p = new URLSearchParams(search);
    const wantsZoom = p.get('zoom') === '1';
    setFormData((f) => ({
      ...f,
      kind: (p.get('kind') as EventKind) || f.kind,
      category: wantsZoom ? 'virtual' : f.category,
      zoomSyncEnabled: wantsZoom ? true : f.zoomSyncEnabled,
    }));
    setShowCreateModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPrefillNew]);

  // Apply the primary type filter before any rendering/grouping.
  const filteredEvents = useMemo(
    () => (events ?? []).filter((e) => kindFilter === 'all' || e.kind === kindFilter),
    [events, kindFilter]
  );

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, Event[]> = {};
    filteredEvents.forEach((event) => {
      const dateKey = new Date(event.startAt).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });

    // Sort each day's events by startAt
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    });

    return grouped;
  }, [filteredEvents]);

  // Get sorted date keys
  const sortedDates = useMemo(() => {
    return Object.keys(eventsByDate).sort();
  }, [eventsByDate]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      kind: 'meeting',
      category: 'virtual',
      startAt: '',
      endAt: '',
      locationName: '',
      locationAddress: '',
      onlineUrl: '',
      zoomSyncEnabled: false,
      eventbriteSyncEnabled: false,
    });
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId) return;

    setCreateError(null);
    try {
      await createEvent({
        workspaceId,
        title: formData.title,
        description: formData.description || undefined,
        kind: formData.kind,
        category: formData.category,
        startAt: new Date(formData.startAt).toISOString(),
        endAt: formData.endAt ? new Date(formData.endAt).toISOString() : undefined,
        locationName: formData.locationName || undefined,
        locationAddress: formData.locationAddress || undefined,
        onlineUrl: formData.onlineUrl || undefined,
        zoomSyncEnabled: formData.zoomSyncEnabled,
        eventbriteSyncEnabled: formData.eventbriteSyncEnabled,
        // Phase D — carry the conversation/contact linkage (creation only; never sends).
        contactId: prefillContactId,
        conversationThreadId: prefillConversationThreadId,
      }).unwrap();
      setShowCreateModal(false);
      resetForm();
      refetch();
      setCreateSuccess(true);
      setTimeout(() => setCreateSuccess(false), 3000);
    } catch (error) {
      const message = error && typeof error === 'object' && 'data' in error
        ? (error as { data?: { error?: string } }).data?.error || 'Failed to create event'
        : 'Failed to create event';
      setCreateError(message);
    }
  };

  const handleDeleteEventClick = (event: Event) => {
    setEventToDelete(event);
  };

  const handleConfirmDeleteEvent = async () => {
    if (!eventToDelete) return;
    try {
      await deleteEvent(eventToDelete.id).unwrap();
      setEventToDelete(null);
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load schedule." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view your schedule.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Feedback */}
      {createSuccess && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          Event created successfully!
        </div>
      )}
      {createError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center justify-between">
          <span>{createError}</span>
          <button onClick={() => setCreateError(null)} className="text-red-600 hover:text-red-800 ml-2">&times;</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
          <p className="text-sm text-gray-500">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
            {kindFilter !== 'all' ? ` · ${KIND_FILTERS.find((f) => f.key === kindFilter)?.label}` : ''}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>New Event</Button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('grouped')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'grouped'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Grouped by Day
          </button>
        </div>

        {/* Primary type filter (All / Meetings / Meetups) — absorbs the old Meetups page */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setKindFilter(f.key)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                kindFilter === f.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2 text-sm">
          <label className="text-gray-600">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <label className="text-gray-600">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Content */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Events Scheduled</h3>
          <p className="text-gray-500 mb-4">Create your first event to get started.</p>
          <Button onClick={() => setShowCreateModal(true)}>Create Event</Button>
        </div>
      ) : viewMode === 'grouped' ? (
        /* Grouped by Day View */
        <div className="space-y-4">
          {sortedDates.map((date) => (
            <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">{formatDateHeader(date)}</h3>
              <div className="space-y-2">
                {eventsByDate[date].map((event) => (
                  <div
                    key={event.id}
                    ref={event.id === deepLinkEventId ? highlightRef : undefined}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-100 transition-colors ${
                      event.id === deepLinkEventId
                        ? 'bg-indigo-50 ring-2 ring-indigo-300'
                        : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-700 w-16 shrink-0">
                        {formatTime(event.startAt)}
                      </span>
                      <button
                        onClick={() => setSelectedEvent(event)}
                        className="text-gray-900 truncate hover:text-blue-600 hover:underline text-left"
                      >
                        {event.title}
                      </button>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${KIND_CONFIG[event.kind].bgColor} ${KIND_CONFIG[event.kind].color}`}>
                        {KIND_CONFIG[event.kind].label}
                      </span>
                      <span className={`text-xs ${CATEGORY_CONFIG[event.category].color}`}>
                        ({CATEGORY_CONFIG[event.category].label})
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {event.zoomJoinUrl && (
                        <a
                          href={event.zoomJoinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Join Zoom
                        </a>
                      )}
                      {event.eventbriteUrl && (
                        <a
                          href={event.eventbriteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                        >
                          Eventbrite
                        </a>
                      )}
                      <button
                        onClick={() => handleDeleteEventClick(event)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    Kind
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Location/URL
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEvents.map((event) => (
                  <tr key={event.id} className={event.id === deepLinkEventId ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">
                        {new Date(event.startAt).toLocaleDateString()}
                      </div>
                      <div className="text-gray-500">{formatTime(event.startAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedEvent(event)}
                        className="font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                      >
                        {event.title}
                      </button>
                      {event.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">{event.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-xs px-2 py-1 rounded-full ${KIND_CONFIG[event.kind].bgColor} ${KIND_CONFIG[event.kind].color}`}>
                        {KIND_CONFIG[event.kind].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`text-sm ${CATEGORY_CONFIG[event.category].color}`}>
                        {CATEGORY_CONFIG[event.category].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                      {event.locationName || event.onlineUrl || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {event.zoomJoinUrl && (
                          <a
                            href={event.zoomJoinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Join Zoom
                          </a>
                        )}
                        {event.eventbriteUrl && (
                          <a
                            href={event.eventbriteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                          >
                            Eventbrite
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteEventClick(event)}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">New Event</h2>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                  <select
                    value={formData.kind}
                    onChange={(e) => setFormData({ ...formData, kind: e.target.value as EventKind })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(KIND_CONFIG).map(([kind, config]) => (
                      <option key={kind} value={kind}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      const category = e.target.value as EventCategory;
                      // Zoom applies only to virtual/hybrid; clear it for in-person.
                      setFormData({
                        ...formData,
                        category,
                        zoomSyncEnabled: category === 'in_person' ? false : formData.zoomSyncEnabled,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => (
                      <option key={cat} value={cat}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
                  <input
                    type="datetime-local"
                    value={formData.startAt}
                    onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                  <input
                    type="datetime-local"
                    value={formData.endAt}
                    onChange={(e) => setFormData({ ...formData, endAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Location fields for in-person/hybrid */}
              {formData.category !== 'virtual' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                    <input
                      type="text"
                      value={formData.locationName}
                      onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.locationAddress}
                      onChange={(e) => setFormData({ ...formData, locationAddress: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Online URL for virtual/hybrid — hidden when Zoom will provision it */}
              {formData.category !== 'in_person' && !formData.zoomSyncEnabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Online URL</label>
                  <input
                    type="url"
                    value={formData.onlineUrl}
                    onChange={(e) => setFormData({ ...formData, onlineUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Sync toggles. Zoom applies only to virtual/hybrid (a provider
                  meeting is provisioned via the canonical Event→Zoom path). */}
              <div className="flex gap-4">
                {formData.category !== 'in_person' && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.zoomSyncEnabled}
                      onChange={(e) => setFormData({ ...formData, zoomSyncEnabled: e.target.checked })}
                      className="rounded text-blue-600"
                    />
                    <span className="text-sm">Create Zoom meeting</span>
                  </label>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.eventbriteSyncEnabled}
                    onChange={(e) => setFormData({ ...formData, eventbriteSyncEnabled: e.target.checked })}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm">Eventbrite sync</span>
                </label>
              </div>
              {formData.zoomSyncEnabled && formData.category !== 'in_person' && (
                <p className="text-xs text-gray-500 -mt-2">
                  A Zoom meeting and join link will be created automatically for this event.
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" isLoading={isCreating} className="flex-1">
                  Create
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{selectedEvent.title}</h2>
              <button onClick={() => setSelectedEvent(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            <div className="space-y-4">
              {/* Date & Time */}
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  {new Date(selectedEvent.startAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {formatTime(selectedEvent.startAt)}
                  {selectedEvent.endAt && ` — ${formatTime(selectedEvent.endAt)}`}
                </span>
              </div>

              {/* Kind & Category */}
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${KIND_CONFIG[selectedEvent.kind].bgColor} ${KIND_CONFIG[selectedEvent.kind].color}`}>
                  {KIND_CONFIG[selectedEvent.kind].label}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full bg-gray-100 ${CATEGORY_CONFIG[selectedEvent.category].color}`}>
                  {CATEGORY_CONFIG[selectedEvent.category].label}
                </span>
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}

              {/* Location */}
              {(selectedEvent.locationName || selectedEvent.locationAddress) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Location</h4>
                  {selectedEvent.locationName && <p className="text-sm text-gray-700">{selectedEvent.locationName}</p>}
                  {selectedEvent.locationAddress && <p className="text-sm text-gray-500">{selectedEvent.locationAddress}</p>}
                </div>
              )}

              {/* Online URL */}
              {selectedEvent.onlineUrl && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Online Link</h4>
                  <a href={selectedEvent.onlineUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                    {selectedEvent.onlineUrl}
                  </a>
                </div>
              )}

              {/* C2 — canonical enrichment: Zoom join/state, RSVP vs observed
                  attendance, recording/transcript availability (progressive). */}
              <EventDetailEnrichment event={selectedEvent} />

              {/* Eventbrite */}
              {selectedEvent.eventbriteSyncEnabled && (
                <div className="p-3 bg-orange-50 rounded-lg">
                  <h4 className="text-xs font-semibold text-orange-700 uppercase mb-2">Eventbrite</h4>
                  {selectedEvent.eventbriteUrl ? (
                    <a
                      href={selectedEvent.eventbriteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700"
                    >
                      View on Eventbrite
                    </a>
                  ) : (
                    <p className="text-sm text-orange-600">
                      {selectedEvent.eventbriteSyncStatus === 'ERROR' ? 'Eventbrite sync failed' : 'Eventbrite sync enabled'}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => setSelectedEvent(null)}
                className="flex-1"
              >
                Close
              </Button>
              <button
                onClick={() => {
                  handleDeleteEventClick(selectedEvent);
                  setSelectedEvent(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
              >
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Event Confirm Dialog */}
      <DeleteConfirmDialog
        isOpen={!!eventToDelete}
        itemName={eventToDelete?.title}
        itemType="event"
        onConfirm={handleConfirmDeleteEvent}
        onCancel={() => setEventToDelete(null)}
      />
    </div>
  );
}
